import { types as eventTypes } from '../enums/event.js';
import subpixel from '../util/canvasSubpixelOffset.js';
import { buildCrosshairData } from '../util/crosshair.js';
import LineTool from './LineTool.js';
import EdgeHandle from './EdgeHandle.js';

/** Crosshair Tool */
export default class CrosshairTool extends LineTool {

	createHandle(mousePanel) {
		const date = this._getHandleDate();
		const valIn = this.panel.yAxis.primaryScale.invert(mousePanel[1]);

		// Always store handle values as actuals
		const value = this.panel.isNormalized
			? this.panel.normalizedToActual(valIn).toFixed(4)
			: valIn.toFixed(4);

		return new EdgeHandle(this, { date, value });
	}

	_emitCrosshairData() {
		const [xCoord] = this.handle[0].getCoords();

		// Don't emit data if tool position hasn't changed
		if (xCoord !== this.state.previousHandleXCoord) {
			this.state.previousHandleXCoord = xCoord;
			const dataPointIndex = this.getDataPointIndexFromX(xCoord);
			const dataPoint = this.core.dataPrimary[dataPointIndex];
			const valueY = dataPoint[this.state.dataPointKey];
			const dataY = subpixel.px(Number(this.panel.yAxis.primaryScale(valueY)));
			// Use setTimeout so as not to block when there are repeated calls to update the crosshair
			setTimeout(
				() => {
					this.core.eventEmitter.emit(
						eventTypes.CROSSHAIR_MOVE,
						buildCrosshairData(
							this.panel,
							dataPoint.date,
							xCoord,
							dataY, // The yCoord should always be the dataY since we're not using the mouse
							dataY
						)
					);
				},
				0
			);
		}
	}

	init(panel, handles) {
		super.init(panel, handles);
		this.state.dataPointKey = this.core.params.customData ? 'value' : 'close';
		this.state.previousHandleXCoord = null;
		this.state.avoidClick = true;

		// Add event listeners
		this.core.eventEmitter.on(eventTypes.DATA_FRAME_UPDATE, this._updateHandleDate.bind(this));
		this.core.eventEmitter.on(eventTypes.SYMBOL_UPDATE, this._resetHandleDate.bind(this));
		this.core.eventEmitter.on(eventTypes.TIME_SERIES_SET, this._manageTimeSeriesSet.bind(this));

	}

	_getDefaultParams() {
		return {
			id: 'crosshair',
			isAutomatic: true,
			name: 'Crosshair Tool',
			startingPosition: 'right',
			style: this.core.getStyle('tool.crosshair')
		};
	}

	_getHandleDate(position = this.params.startingPosition) {
		const [leftIndex, rightIndex] = this.core.xAxis.primaryScale.domain();

		switch (position) {
			case 'left':
				// If date is off of the chart, then move to the right by 1 date
				return this.core.exchangeDates[Math.ceil(leftIndex)] >= this.core.dataFrame[0].date
					? this.core.exchangeDates[Math.ceil(leftIndex) + 1]
					: this.core.dataFrame[0].date;
			case 'right':
				// If date is off of the chart, then move to the left by 1 date
				return this.core.exchangeDates[Math.floor(rightIndex)] <= this.core.dataFrame[this.core.dataFrame.length - 1].date
					? this.core.exchangeDates[Math.floor(rightIndex) - 1]
					: this.core.dataFrame[this.core.dataFrame.length - 1].date;
			default:
				// Use the middle date
				return this.core.dataFrame[Math.round(this.core.dataFrame.length / 2)].date; // The middle of the chart data
		}
	}

	_manageTimeSeriesSet(args) {
		if (args.updatedProperty === 'days') this._resetHandleDate();
	}

	onClickCanvas(mouse) {
		let secondClick = false;

		// called from core.onClick() on both first and second clicks.
		if (!this.state.inProgressHandle) {
			// first click - create and render first handle
			this.handle[0] = this.createHandle([mouse[0], mouse[1] - this.panel.size.top]);
			this.handle[0].state.visible = true;

			if (this.state.avoidClick) {
				this.state.avoidClick = false;
				secondClick = true;
			}
		} else {
			secondClick = true;
		}

		if (secondClick) {
			this.state.inProgressHandle = false; // no longer actively drawing a change in the tool
			this.state.dragHandle = null;
			this.onComplete();
		}
	}

	remove() {
		// Remove event listeners
		this.core.eventEmitter.off(eventTypes.DATA_FRAME_UPDATE, this._updateHandleDate);
		this.core.eventEmitter.off(eventTypes.SYMBOL_UPDATE, this._resetHandleDate);
		this.core.eventEmitter.off(eventTypes.TIME_SERIES_SET, this._manageTimeSeriesSet);

		super.remove();
	}

	render() {
		const [start, stop] = this.panel.xAxis.primaryScale.domain();

		// Skip rendering when no domain has been set to avoid showing crosshair in wrong location
		if (!(start === 0 && stop === 0)) {
			this._renderHorizontal(this.panel);
			this._renderVertical(this.panel);
			this.handle[0].render();
			this._emitCrosshairData();
			this.panel.core.onToolRender(this);
		}
	}

	_renderHorizontal(panel) {
		const { core } = this;
		const { style } = this.params;
		const ctx = panel.rootToolContext;
		const coords = this.handle[0].getCoords();
		const dataPointIndex = this.getDataPointIndexFromX(coords[0]);
		const dataPoint = core.dataPrimary[dataPointIndex];
		const dataY = subpixel.px(Number(this.panel.yAxis.primaryScale(dataPoint[this.state.dataPointKey])));

		ctx.save();
		ctx.beginPath();
		ctx.strokeStyle =
			style.horizontal.color ||
			style.default.color;
		ctx.lineWidth =
			style.horizontal.width ||
			style.default.width;
		const lineDash =
			style.horizontal.lineDash ||
			style.default.lineDash ||
			[];
		ctx.setLineDash(lineDash.split(/\s*,\s*/));
		ctx.moveTo(0, dataY);
		ctx.lineTo(this.panel.size.width, dataY);
		ctx.stroke();
		ctx.restore();
	}

	/** Alternate render method for this tool on lower panels (just render the line) */
	renderLower(panel) {
		this._renderVertical(panel);
	}

	_renderVertical(panel) {
		const { style } = this.params;
		const ctx = panel.rootToolContext;
		const coords = this.handle[0].getCoords();

		ctx.save();
		ctx.beginPath();
		ctx.strokeStyle =
			style.vertical.color ||
			style.default.color;
		ctx.lineWidth =
			style.vertical.width ||
			style.default.width;
		const lineDash =
			style.vertical.lineDash ||
			style.default.lineDash ||
			[];
		ctx.setLineDash(lineDash.split(/\s*,\s*/));
		ctx.moveTo(coords[0], 0);
		ctx.lineTo(coords[0], panel.size.height);
		ctx.stroke();
		ctx.restore();
	}

	_resetHandleDate() {
		this.state.resetHandle = true;
	}

	_updateHandleDate() {
		const handle = this.handle[0];

		// Reset handle date to starting position, or move to left/right per getHandleDate
		if (this.state.resetHandle) {
			this.state.resetHandle = false;
			handle.params.date = new Date(this._getHandleDate());
		} else if (handle.params.date < this.core.dataFrame[0].date) {
			handle.params.date = new Date(this._getHandleDate('left'));
		} else if (handle.params.date > this.core.dataFrame[this.core.dataFrame.length - 1].date) {
			handle.params.date = new Date(this._getHandleDate('right'));
		}
	}

}
